import { useEffect, useState } from "react";
import useAction from "../AccountMode/useAction";
import { Typography, Button } from "@blofin/blofin-ui";

import { useStoreState } from "../store/useStore";
import { useTranslations } from "use-intl";
import { numberFormat, transformUSDT } from "../utils/index";
import useWatchLoaclstorageValue from "./useWatchLoaclstorageValue";

const defaultRate = "USD";

interface FiatList {
  name: string;
  precision: string;
  rate: string;
  symbol: string;
}

const defaultCrypto = {
  usdt_usd: "1",
  name: "USD",
  precision: "0.01",
  rate: "1",
  symbol: "$"
};

const Card = ({ accountType, visible }: { visible: boolean; accountType: string | undefined }) => {
  const { getUserAsset, getExchangeRate } = useAction();
  const [userAsset, setUserAsset] = useState("0");
  const [loading, setLoading] = useState(true);
  const [isShow, setIsShow] = useState(true);
  const [crypto, setCrypto] = useState(defaultCrypto);

  const t = useTranslations("header");
  const { localeLink } = useStoreState();

  const { isDemoTrading } = useStoreState();

  const { updateLocalStorage, value: localstorageVisibility } = useWatchLoaclstorageValue({
    key: "assets-isHideTotalAssets",
    initValue: isShow ? "1" : "0"
  });

  const initAccountInfo = async () => {
    try {
      const res = await getUserAsset();
      const { code, msg, data } = res as unknown as {
        code: number;
        msg: string;
        data: { total_equity: string };
      };
      setLoading(false);
      if (code !== 200) {
        throw new Error(msg || "Get Account Mode Error");
      }
      setUserAsset(data.total_equity);
    } catch (error) {
      console.log("Get Account Mode Error", error);
    }
  };

  const getRate = async () => {
    try {
      const res = await getExchangeRate<{
        code: number;
        data: { fiat: FiatList[]; usdt_usd: string };
      }>();
      const { code, data } = res || {};
      if (code == 200) {
        const { usdt_usd = "", fiat = [] } = data || {};
        const USDInfo = fiat.find((item) => item.name === defaultRate) || defaultCrypto;
        setCrypto({
          usdt_usd,
          ...USDInfo
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  const changeShow = () => {
    const newValue = !isShow;
    setIsShow(newValue);
    updateLocalStorage(newValue ? "1" : "0");
  };

  useEffect(() => {
    const newValue = localstorageVisibility === "0" ? false : true;
    setIsShow(newValue);
  }, [localstorageVisibility]);

  useEffect(() => {
    if (visible) {
      getRate();
      initAccountInfo();
    }
  }, [visible]);

  const isShowButtonRange = accountType === "MAIN";

  if (isDemoTrading) return <div className="pt-[24px]"></div>;

  return (
    <div className="p-[24px] pb-[16px]">
      <div
        className="cursor-pointer group/assetcard"
        onClick={() => (window.location.href = `${localeLink}/assets/overview`)}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center justify-start">
            <Typography variant="body4" className="text-light-label-40 leading-[18px]">
              {t("overview")}
            </Typography>
            {isShow ? (
              <i
                className="ml-1 iconfont icon-show-fill text-[14px] text-light-label-40 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  changeShow();
                }}
              ></i>
            ) : (
              <i
                className="ml-1 iconfont icon-hide-line text-[14px] text-light-label-40 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  changeShow();
                }}
              ></i>
            )}
          </div>
          <div className="w-[28px] h-[28px] items-start justify-end relative top-[6px] cursor-pointer">
            <div className="text-right">
              <i className="iconfont icon-arrow-right-l-line text-[16px] text-light-label-40 group-hover/assetcard:text-light-label"></i>
            </div>
          </div>
        </div>
        <div className="mt-1">
          <Typography variant="subtitle1" weight="bold" className="leading-[34px]">
            {isShow ? `${loading ? "--" : numberFormat(userAsset, 2)}` : "******"} USDT
          </Typography>
        </div>
        <div className="">
          <Typography variant="body2" className="leading-[24px] text-light-label-40">
            {isShow
              ? `≈ ${loading ? "--" : transformUSDT(userAsset, Number(crypto.usdt_usd) ? Number(crypto.usdt_usd) : 1, Number(crypto.rate) ? Number(crypto.rate) : 1, crypto.precision, crypto.symbol)}`
              : "******"}
          </Typography>
        </div>
      </div>
      {isShowButtonRange && (
        <div className="mt-[24px] gap-[12px] flex items-center justify-center">
          <Button
            variant="primary"
            size="medium"
            className="w-[138px]"
            onClick={() => (window.location.href = `${localeLink}/assets/deposit`)}
          >
            {t("deposit")}
          </Button>
          <Button
            variant="ghost"
            size="medium"
            className="w-[138px]"
            onClick={() => (window.location.href = `${localeLink}/assets/withdraw`)}
          >
            {t("withdraw")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Card;
